import { useEffect, useState } from 'react';
import NavbarV2 from '../../components/navbarV2/NavbarV2';
import Footer from '../../components/footer/Footer';
import '../home/HomeV2.css';
import './ProductV2.css';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux'
import { add } from '../../redux/cartSlice'
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaBoxOpen, FaShieldAlt, FaTruck } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import { productListingSubtitle } from '../../utils/ikeaCatalogSubtitle';
import { formatMoney } from '../../utils/formatMoney';
import { ikeaImageSrc } from '../../utils/ikeaImage';

const isIkeaMultiPackId = (sku) => /^s/i.test(String(sku || '').trim());

const countPackageDimensionRows = (dimensions) => {
    if (!Array.isArray(dimensions)) return 0;
    return dimensions.reduce(
        (total, dim) => total + (Array.isArray(dim.packages) ? dim.packages.length : 0),
        0,
    );
};

const formatPackSize = (pack) => {
    if (pack.width != null && pack.height != null && pack.depth != null) {
        return `${pack.width}×${pack.height}×${pack.depth} cm`;
    }
    if (pack.depth != null && pack.cross != null) {
        return `${pack.depth}×${pack.cross} cm`;
    }
    return null;
};

const ProductV2 = () => {
    const params = useParams();
    const [customProduct, setCustomProduct] = useState(false);
    const [stock, setStock] = useState(null);
    const [probability, setProbability] = useState('');
    const [stockLoading, setStockLoading] = useState(false);
    const [stockError, setStockError] = useState(false);
    const [id, setId] = useState('');
    const [product, setProduct] = useState(null);
    const [amount, setAmount] = useState(1);
    const [ikeaDimensions, setIkeaDimensions] = useState(null);
    const [ikeaPackagesLoading, setIkeaPackagesLoading] = useState(false);
    const [ikeaPackagesError, setIkeaPackagesError] = useState(null);
    const [ikeaPackageSource, setIkeaPackageSource] = useState(null);

    const dispatch = useDispatch()

    const navigate = useNavigate();

    useEffect(() => {
        if (params.id) {
            setId(params.id);
        } else {
            navigate('/');
        }
    }, [params, navigate])

    useEffect(() => {
        if (id) {
            if (id.length > 20) {
                setCustomProduct(true);

                fetch(`${process.env.REACT_APP_API}/api/getCustomProduct/${id}`, {})
                .then(response => {
                    if (response.status === 200) {
                        response.json().then(data => setProduct(data));
                    }
                })
            } else {
                setCustomProduct(false);
                fetch(`${process.env.REACT_APP_API}/api/getProduct/${id}`, {})
                .then(response => {
                    if (response.status === 200) {
                        response.json().then(data => {
                            setProduct(data);
                        });
                    }
                })
            }
        }
    }, [id])

    useEffect(() => {
        setIkeaDimensions(null);
        setIkeaPackagesError(null);
        setIkeaPackageSource(null);

        const articleId = id || params.id;
        const isMp = !!articleId && !customProduct && isIkeaMultiPackId(articleId);
        if (!isMp) {
            setIkeaPackagesLoading(false);
            return undefined;
        }

        const controller = new AbortController();
        let cancelled = false;

        async function fetchPackages() {
            setIkeaPackagesLoading(true);
            setIkeaPackagesError(null);

            try {
                const response = await fetch(`${process.env.REACT_APP_API}/api/resolveIkeaPackages`, {
                    method: 'POST',
                    signal: controller.signal,
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        articleId: String(articleId).trim(),
                    }),
                });

                let body = null;
                try {
                    body = await response.json();
                } catch {
                    body = {};
                }

                if (cancelled) return;

                setIkeaPackageSource(body?.source || null);

                if (
                    response.ok &&
                    Array.isArray(body.dimensions) &&
                    body.dimensions.length > 0
                ) {
                    setIkeaDimensions(body.dimensions);
                } else {
                    setIkeaDimensions([]);
                    setIkeaPackagesError(
                        typeof body.error === 'string'
                            ? body.error
                            : 'Could not load package breakdown.'
                    );
                }
            } catch (e) {
                if (cancelled || e?.name === 'AbortError') return;
                setIkeaDimensions([]);
                setIkeaPackagesError('Could not load package breakdown.');
            } finally {
                if (!cancelled) setIkeaPackagesLoading(false);
            }
        }

        fetchPackages();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [params.id, id, customProduct]);
    
    useEffect(() => {
        if (!product || id.length > 20) {
            return;
        }
        setStockLoading(true);
        setStockError(false);
        fetch(`${process.env.REACT_APP_API}/api/ikea/stock/${encodeURIComponent(id)}`)
            .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
            .then(({ ok, data }) => {
                if (ok && data.ok) {
                    setStock(data.stock);
                    setProbability(data.probability || '');
                } else {
                    setStock(null);
                    setProbability('');
                    setStockError(true);
                }
            })
            .catch(() => {
                setStock(null);
                setProbability('');
                setStockError(true);
            })
            .finally(() => {
                setStockLoading(false);
            });
    }, [product, id]);

    const handleChange = (e) => {
        e.preventDefault;

        setAmount(e.target.value);
    }

    return (
        <div className="v2-page productv2">
            <NavbarV2 />

            {product && (
            <div className="v2-container">
                <div className="productv2-layout">
                {customProduct ? (
                    <div className="productv2-gallery">
                    <div className="productv2-slide">
                        <img src={product.img} alt={product.name} className="productv2-main-img" />
                    </div>
                </div>
                ) : (
                    <div className="productv2-gallery">
                    <div className="productv2-slide">
                        <img src={ikeaImageSrc(product.image)} alt={product.imageAlt || product.name} className="productv2-main-img" />
                    </div>
                    <div className="productv2-slide productv2-slide--secondary">
                        <img src={ikeaImageSrc(product.contextualImageUrl)} alt={product.imageAlt || product.name} className="productv2-alt-img" />
                    </div>
                    </div>
                )}
                <div className="productv2-detail">
                    <h4>{customProduct ? product.name : product.name}</h4>
                    <p>
                        {customProduct ? product.description : productListingSubtitle(product)}
                    </p>
                    <h2>€{customProduct ? formatMoney(product.price) : formatMoney(product.price.currentPrice)}</h2>

                    <div className="productv2-info-stack">
                        {stockLoading && (
                            <p className="productv2-stock-line productv2-stock-line--loading">
                                <ClipLoader color="#C4603C" size={14} />
                                Checking stock…
                            </p>
                        )}
                        {!stockLoading && stockError && (
                            <p className="productv2-stock-line">Stock check temporarily unavailable.</p>
                        )}
                        {!stockLoading && !stockError && stock === null && (
                            <p className="productv2-stock-line">No stock info available.</p>
                        )}
                        {!stockLoading && !stockError && stock !== null && (
                            <p className="productv2-stock-line">
                                {stock} items in stock.{' '}
                                {probability === 'HIGH_IN_STOCK' ? (
                                    <small className="productv2-stock-hint productv2-stock-hint--high">
                                        High in stock.
                                    </small>
                                ) : probability === 'LOW_IN_STOCK' ? (
                                    <small className="productv2-stock-hint productv2-stock-hint--low">
                                        Low items remaining.
                                    </small>
                                ) : (
                                    <small className="productv2-stock-hint productv2-stock-hint--out">
                                        Out of stock!
                                    </small>
                                )}
                            </p>
                        )}
                        {!customProduct && !stockLoading && (
                            <div className="productv2-callout productv2-callout--delivery">
                                Expected to arrive in Malta in 2 weeks subject to availability at IKEA Catania.
                            </div>
                        )}
                        <div className="productv2-callout productv2-callout--schedule">
                            Delivered in 1 week time from order confirmation as per deliveries schedule terms and
                            conditions apply.
                        </div>
                    </div>
                    {!customProduct && isIkeaMultiPackId(id || params.id) && (
                            <section className="product-packages-breakdown productv2-packages-wrap" aria-label="IKEA packages">
                                <details className="productv2-packages-disclosure">
                                    <summary className="productv2-packages-summary">
                                        <span className="productv2-packages-summary-title">Packages</span>
                                        {!ikeaPackagesLoading &&
                                            Array.isArray(ikeaDimensions) &&
                                            ikeaDimensions.length > 0 && (
                                                <span className="productv2-packages-summary-meta">
                                                    {countPackageDimensionRows(ikeaDimensions)} carton
                                                    {countPackageDimensionRows(ikeaDimensions) === 1 ? '' : 's'}
                                                </span>
                                            )}
                                    </summary>
                                    <div className="productv2-packages-panel">
                                <p className="product-packages-sub">
                                    Bundled IKEA article shipped in multiple cartons — component SKU and carton
                                    sizes below.
                                    {ikeaPackageSource === 'batch_extract' && (
                                        <> <span className="muted">(source: extract)</span></>
                                    )}
                                    {ikeaPackageSource === 'html' && (
                                        <> <span className="muted">(source: product page)</span></>
                                    )}
                                </p>
                                {ikeaPackagesLoading && (
                                    <p className="product-packages-msg">Loading package details…</p>
                                )}
                                {!ikeaPackagesLoading &&
                                    ikeaPackagesError &&
                                    !(Array.isArray(ikeaDimensions) && ikeaDimensions.length > 0) && (
                                        <p className="product-packages-msg error">{ikeaPackagesError}</p>
                                    )}
                                {!ikeaPackagesLoading &&
                                    Array.isArray(ikeaDimensions) &&
                                    ikeaDimensions.length > 0 && (
                                    <>
                                        <p className="product-packages-total-inline productv2-packages-dim-rows">
                                            Dimension rows:{' '}
                                            <strong>{countPackageDimensionRows(ikeaDimensions)}</strong>
                                        </p>
                                        {ikeaDimensions.map((dimension, dimIndex) => {
                                            const componentLine = [dimension.componentName, dimension.componentType]
                                                .filter(Boolean)
                                                .join(' · ');
                                            const thumbAlt =
                                                (typeof dimension.imageAlt === 'string' && dimension.imageAlt.trim()) ||
                                                (componentLine
                                                    ? componentLine.replace(/\s+/g, ' ').trim()
                                                    : '');
                                            const defaultAlt =
                                                thumbAlt ||
                                                `Component ${dimension.sku != null ? dimension.sku : ''}`.trim();
                                            return (
                                        <div
                                            key={`${dimension.sku || dimIndex}-${dimIndex}`}
                                            className="product-packages-dimension"
                                        >
                                            <div className="product-packages-dimension-inner">
                                                {dimension.image ? (
                                                    <div className="product-packages-thumb-wrap">
                                                        <img
                                                            src={ikeaImageSrc(dimension.image)}
                                                            alt={defaultAlt}
                                                            className="product-packages-thumb"
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                ) : null}
                                                <div className="product-packages-dimension-copy">
                                            {componentLine ? (
                                                <div className="product-packages-component-title">
                                                    {dimension.componentUrl ? (
                                                        <a
                                                            href={dimension.componentUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="product-packages-component-link"
                                                        >
                                                            {componentLine}
                                                        </a>
                                                    ) : (
                                                        <span>{componentLine}</span>
                                                    )}
                                                </div>
                                            ) : null}
                                            {dimension.sku && (
                                                <div className="product-packages-component productv2-component-meta">
                                                    <div className="productv2-component-sku-line">
                                                        <span className="product-packages-component-sku">
                                                            Component SKU:{' '}
                                                            <span className="productv2-sku-pill">{dimension.sku}</span>
                                                        </span>
                                                    </div>
                                                    {Array.isArray(dimension.packages) ? (
                                                        <div className="muted productv2-component-rows-meta">
                                                            {dimension.packages.length} row(s)
                                                        </div>
                                                    ) : null}
                                                </div>
                                            )}
                                            {Array.isArray(dimension.packages) &&
                                                dimension.packages.length > 0 && (
                                                    <div className="productv2-carton-grid">
                                                        {dimension.packages.map((pack, packIndex) => {
                                                            const subSku =
                                                                pack.articleNumber || dimension.sku || '—';
                                                            const sizeLabel = formatPackSize(pack);
                                                            const many = dimension.packages.length > 1;
                                                            return (
                                                                <div
                                                                    key={`${dimIndex}-${packIndex}-${
                                                                        pack.articleNumber ?? packIndex
                                                                    }`}
                                                                    className="productv2-carton-card"
                                                                >
                                                                    <div className="productv2-carton-card-head">
                                                                        <span className="productv2-carton-index">
                                                                            {many
                                                                                ? `Box ${packIndex + 1} of ${
                                                                                      dimension.packages.length
                                                                                  }`
                                                                                : 'Shipping package'}
                                                                        </span>
                                                                        <span className="productv2-carton-sku-badge" title="Carton article number">
                                                                            {subSku}
                                                                        </span>
                                                                    </div>
                                                                    <ul className="productv2-carton-specs">
                                                                        {sizeLabel ? (
                                                                            <li>
                                                                                <span className="productv2-carton-k">
                                                                                    W×H×D
                                                                                </span>
                                                                                <span className="productv2-carton-v">
                                                                                    {sizeLabel}
                                                                                </span>
                                                                            </li>
                                                                        ) : null}
                                                                        {pack.weight != null ? (
                                                                            <li>
                                                                                <span className="productv2-carton-k">
                                                                                    Weight
                                                                                </span>
                                                                                <span className="productv2-carton-v">
                                                                                    {pack.weight} kg
                                                                                </span>
                                                                            </li>
                                                                        ) : null}
                                                                        {many && pack.packages != null ? (
                                                                            <li>
                                                                                <span className="productv2-carton-k">
                                                                                    Boxes
                                                                                </span>
                                                                                <span className="productv2-carton-v">
                                                                                    {pack.packages}
                                                                                </span>
                                                                            </li>
                                                                        ) : null}
                                                                    </ul>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                        </div>
                                            );
                                        })}
                                    </>
                                    )}
                                {!ikeaPackagesLoading &&
                                    ikeaDimensions?.length === 0 &&
                                    !ikeaPackagesError && (
                                        <p className="product-packages-msg">No breakdown available.</p>
                                    )}
                                    </div>
                                </details>
                            </section>
                        )}
                    {!customProduct && product.variants?.length > 0 && (
                        <div className="productv2-colors">
                        <h4 className="productv2-colors-title">Choose variation</h4>
                        <div className="productv2-variant-row">
                            {product.variants.map((variant) => (
                                <button
                                    type="button"
                                    key={variant.id}
                                    className="productv2-variant-btn"
                                    onClick={() => navigate(`/product/${variant.id}`)}
                                >
                                    <img src={ikeaImageSrc(variant.image)} alt="" className="productv2-variant-img" />
                                </button>
                            ))}
                        </div>
                        </div>
                    )}
                    <p className="productv2-sku">{customProduct ? product._id : product.id.replace(/(\d{3})(?=\d)/g, '$1.')}</p>

                    <div className="productv2-tools">
                        <input className="productv2-qty" type="number" min="1" value={amount} onChange={handleChange} />
                        <button
                            type="button"
                            className="v2-btn v2-btn--secondary productv2-add"
                            onClick={() => {
                                dispatch(add({
                                    id: params.id,
                                    name: customProduct ? product.name : product.name,
                                    price: customProduct ? product.price : product.price.currentPrice,
                                    img: customProduct ? product.img : product.image,
                                    amount,
                                }))
                                toast.success(`${customProduct ? product.name : product.name} added to cart!`)
                            }}
                        >
                            Add to cart
                        </button>
                    </div>
                </div>
                </div>
            </div>
            )}

            <section className="homev2-value-props productv2-value-band" aria-label="Why Sicily Shopping">
                <div className="homev2-value-inner">
                    <div className="homev2-value-item">
                        <div className="homev2-value-icon">
                            <FaTruck />
                        </div>
                        <h4 className="homev2-value-title">Weekly Deliveries</h4>
                        <p className="homev2-value-desc">
                            Scheduled weekly deliveries across Malta and Gozo, right to your door.
                        </p>
                    </div>
                    <div className="homev2-value-item">
                        <div className="homev2-value-icon">
                            <FaBoxOpen />
                        </div>
                        <h4 className="homev2-value-title">No Minimum Order</h4>
                        <p className="homev2-value-desc">
                            Order any amount. Orders under €300 include a €25 delivery fee.
                        </p>
                    </div>
                    <div className="homev2-value-item">
                        <div className="homev2-value-icon">
                            <FaShieldAlt />
                        </div>
                        <h4 className="homev2-value-title">Secure Payments</h4>
                        <p className="homev2-value-desc">
                            Safe and secure online payments for all your orders.
                        </p>
                    </div>
                </div>
            </section>

            <Footer hero={true} />
        </div>
    );
}

export default ProductV2;